import type { WernessParams, WernessParamsC } from '../types';
import { BLUE_NOISE_128, BLUE_NOISE_SIZE } from '../util/blue-noise-data';

const W_LEFT = 7 / 16;
const W_TOP_LEFT = 3 / 16;
const W_TOP = 5 / 16;
const W_TOP_RIGHT = 1 / 16;

/**
 * Werness "inside-out" dithering.
 *
 * Per-pixel raster scan. Each pixel's threshold is the blue noise value
 * adjusted by a pull from already-processed neighbors' errors:
 *   threshold = blue_noise[x, y] - sum(weight_i * neighbor_error_i)
 *
 * The pull weights mirror Floyd-Steinberg's distribution (7/16, 3/16, 5/16, 1/16)
 * but applied backward: each pixel reads from its left and top neighbors.
 *
 * Brightness shifts the input gray by [-1, 1]. Contrast scales around 0.5.
 *
 * Reference: Lucas Pope, "Papers Please" devlog on dithering;
 *            Surma's blue-noise/inside-out section in the Ditherpunk article.
 */
export function wernessMask(
  source: ImageData,
  params: WernessParams | WernessParamsC,
): Uint8Array {
  const { density, brightness, contrast } = params;
  const w = source.width;
  const h = source.height;
  const buffer = new Float32Array(w * h);
  const errBuffer = new Float32Array(w * h);

  const densityShift = (50 - density) / 50;
  const brightnessShift = brightness / 50;
  const contrastFactor = 1 + contrast / 50;
  const src = source.data;

  for (let i = 0; i < w * h; i++) {
    let g = (src[i * 4] ?? 0) / 255;
    g = (g - 0.5) * contrastFactor + 0.5;
    g += brightnessShift;
    g += densityShift;
    if (g < 0) g = 0;
    else if (g > 1) g = 1;
    buffer[i] = g;
  }

  const mask = new Uint8Array(w * h);

  for (let y = 0; y < h; y++) {
    const ty = y % BLUE_NOISE_SIZE;
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const tx = x % BLUE_NOISE_SIZE;
      let threshold = ((BLUE_NOISE_128[ty * BLUE_NOISE_SIZE + tx] ?? 0) + 0.5) / 256;

      let pulled = 0;
      if (x > 0) pulled += W_LEFT * (errBuffer[i - 1] ?? 0);
      if (y > 0 && x > 0) pulled += W_TOP_LEFT * (errBuffer[i - w - 1] ?? 0);
      if (y > 0) pulled += W_TOP * (errBuffer[i - w] ?? 0);
      if (y > 0 && x < w - 1) pulled += W_TOP_RIGHT * (errBuffer[i - w + 1] ?? 0);

      threshold -= pulled;

      const gray = buffer[i] ?? 0;
      const output = gray > threshold ? 1 : 0;
      mask[i] = output;
      errBuffer[i] = gray - output;
    }
  }

  return mask;
}
